export type Category = {
  slug: string;
  name: string;
  short: string;
  description: string;
  cover: string;
};

export type Article = {
  id: string;
  title: string;
  excerpt: string;
  cover: string;
  categorySlug: string;
  categoryName: string;
  author: string;
  date: string;
  readTime: number;
  tags: string[];
  heat: number;
  views: string;
  featured?: boolean;
  content: string[];
  timeline?: { time: string; text: string }[];
};

export type Faq = {
  q: string;
  a: string;
};

export const categories: Category[] = [
  {
    slug: "idol-meltdown",
    name: "偶像塌房",
    short: "人设崩塌现场",
    description:
      "从路透照到工作室声明，记录偶像人设崩塌的完整时间线，也聊聊塌房背后粉丝与经纪公司之间那笔算不清的账。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20scene%20of%20a%20dim%20rehearsal%20room%20with%20a%20single%20spotlight%20and%20empty%20microphone%20stand%2C%20cinematic%20moody%20atmosphere%2C%20warm%20crimson%20and%20gold%20tones%2C%20magazine%20cover%20illustration%20style%2C%20soft%20grain%2C%20high%20detail&width=640&height=420&seq=mrcg-cat-idol&orientation=landscape",
  },
  {
    slug: "actor-rumors",
    name: "演员绯闻",
    short: "剧组内外情感线",
    description:
      "剧组同框、深夜接送、合作传闻，我们把演员绯闻里的蛛丝马迹按时间顺序摊开，给你一个更冷静的观看角度。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20film%20set%20at%20dusk%20with%20director%20chair%2C%20clapperboard%20and%20warm%20backlight%2C%20vintage%20cinema%20aesthetic%2C%20muted%20amber%20and%20terracotta%20palette%2C%20magazine%20feature%20visual%2C%20soft%20texture&width=640&height=420&seq=mrcg-cat-actor&orientation=landscape",
  },
  {
    slug: "singer-flops",
    name: "歌手翻车",
    short: "现场与音轨疑云",
    description:
      "现场走音、音轨对比、临时取消演出，歌手翻车向来最能引爆讨论。我们拆解舞台上的每一个细节，让判断有据可依。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20concert%20stage%20seen%20from%20the%20wings%20with%20dramatic%20golden%20spotlights%20and%20hazy%20smoke%2C%20silhouette%20of%20a%20singer%20mid%20performance%2C%20warm%20amber%20tones%2C%20artistic%20music%20magazine%20illustration%2C%20cinematic%20depth&width=640&height=420&seq=mrcg-cat-singer&orientation=landscape",
  },
  {
    slug: "variety-moments",
    name: "综艺名场面",
    short: "剪辑与真实的缝隙",
    description:
      "被剪掉的镜头、被放大的表情、被剪进预告的冲突。综艺名场面的讨论度从来不只是节目本身，还有观众的解读。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20a%20colorful%20television%20studio%20with%20glowing%20lights%20and%20stage%20curtains%2C%20playful%20editorial%20energy%2C%20warm%20red%20orange%20and%20cream%20palette%2C%20retro%20variety%20show%20poster%20style%2C%20grainy%20texture&width=640&height=420&seq=mrcg-cat-variety&orientation=landscape",
  },
];

export const articles: Article[] = [
  {
    id: "lin-wanxing-night-meeting-timeline",
    title: "林晚星深夜密会事件全复盘：从路透照到工作室回应的 72 小时",
    excerpt:
      "一张模糊的地下车库路透照，把「林晚星」推上了三天热搜。我们按时间顺序把流出的每一张图、每一次回应重新排了一遍，还原这场舆论风波是怎么一步步失控又降温的。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20night%20city%20underground%20parking%20garage%20with%20harsh%20flash%20light%20and%20blurred%20figures%20walking%20away%2C%20cinematic%20documentary%20mood%2C%20warm%20crimson%20and%20amber%20tones%2C%20entertainment%20magazine%20cover%20illustration%2C%20soft%20film%20grain&width=900&height=560&seq=mrcg-art-01&orientation=landscape",
    categorySlug: "idol-meltdown",
    categoryName: "偶像塌房",
    author: "吃瓜编辑室",
    date: "2026-09-09",
    readTime: 6,
    tags: ["偶像塌房", "热搜"],
    heat: 98,
    views: "128.6万",
    featured: true,
    content: [
      "周一晚十点，一张画质并不理想的地下车库路透照开始在小范围传播。照片里两个戴着口罩的身影一前一后走向同一辆车，拍摄角度来自对面的消防通道。最初愿意转发的人并不多，因为画面里能辨认的信息实在有限，连背影都难以确认。",
      "真正的转折发生在凌晨。有人翻出同一时段附近的便利店监控截图，把画面里的行李箱颜色、行进方向与路透照做了比对。这套「民间侦探式」的推理在几个小时内冲上热搜，讨论量从几千条涨到了几十万条。",
      "第二天上午，相关词条的讨论逐渐从「是不是本人」转向「她是不是在谈恋爱」。这个转向很关键：一旦话题从「事实」变成「人设」，争论的双方就很难再回到同一套评判标准上。粉丝在意的是承诺与预期，路人关心的则是有没有隐瞒。",
      "傍晚，工作室发布了一份两百字左右的声明，措辞克制，既没有正面承认，也没有完全否认，只强调「私人行程不便公开」。这份声明的效果可以说是两极分化：一部分人认为回应得体、保护了当事人；另一部分人则认为含糊其辞本身就是一种态度。",
      "到第三天，热度开始自然回落。新的娱乐话题顶了上来，讨论重心也从事件本身转移到「艺人到底有没有必要公开私人生活」这类更宏观的议题上。回头看这七十二小时，真正被反复消费的其实不是那几张照片，而是围观者对人设的期待与失望。",
      "对我们来说，值得记录的不是谁和谁走在一起，而是一场舆论如何从模糊信息出发，经由推理、站队、回应，最终走向疲惫。这个过程本身，或许比任何一张路透照都更有观察价值。",
    ],
    timeline: [
      { time: "周一 22:14", text: "首张地下车库路透照在小范围传播，画质模糊，无法确认人物身份。" },
      { time: "周二 01:30", text: "网友比对监控截图与行李箱细节，话题首次冲上热搜前十。" },
      { time: "周二 09:40", text: "讨论焦点从「是否本人」转向「是否在恋爱」，粉丝站陆续发声。" },
      { time: "周二 19:05", text: "工作室发布简短声明，强调私人行程不公开，未正面回应传闻。" },
      { time: "周三 14:20", text: "新话题顶上热搜，讨论热度回落，事件进入长尾阶段。" },
    ],
  },
  {
    id: "boy-band-romance-rumor-fansites",
    title: "顶流男团成员疑似恋情曝光，粉丝站集体关站背后的应援经济学",
    excerpt:
      "恋情传闻发酵不到一天，三个大型粉丝站先后宣布关站。表面是情绪化的脱粉，背后却是一套运行多年的应援投入与回报逻辑在瞬间失衡。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20empty%20fan%20support%20banners%20and%20glow%20sticks%20left%20behind%20in%20a%20dark%20arena%20after%20a%20concert%2C%20melancholic%20warm%20crimson%20lighting%2C%20editorial%20entertainment%20magazine%20visual%2C%20cinematic%20grain&width=900&height=560&seq=mrcg-art-02&orientation=landscape",
    categorySlug: "idol-meltdown",
    categoryName: "偶像塌房",
    author: "白桃",
    date: "2026-09-08",
    readTime: 7,
    tags: ["偶像塌房", "粉丝文化"],
    heat: 94,
    views: "96.2万",
    content: [
      "关站公告写得很短，只有一句「感谢这些年，后会有期」。但熟悉圈子的人都知道，一个运营了五年、累计投入六位数的粉丝站选择关门，几乎不可能只是因为一条未经证实的传闻。",
      "粉丝站本质上是「用爱发电」的志愿组织，但它的运转需要真金白银：应援物的印刷、线下活动的场地、生日应援的大屏投放，每一项都要靠集资完成。投入的越多，站子与艺人之间的情感绑定就越深，容错空间也越小。",
      "所以当恋情传闻出现时，动摇的往往不是「喜欢」这件事本身，而是长期建立的「我们是在一起成长的」这种叙事。粉丝投入的不只是钱，还有身份认同和时间。塌房的痛感，很多时候来自这种认同感的突然失重。",
      "值得注意的是，这次关站的三家粉丝站，运营者都是接近三十岁的资深粉丝。她们在公告里没有指责艺人，反而更多地是在告别自己过去几年的状态。这或许是这一轮脱粉潮与以往最大的不同：离开的理由更私人，也更平静。",
      "对围观者来说，与其把粉丝的选择简单归结为「玻璃心」，不如把它看作一次关于情感投入与回报的真实样本。任何长期关系，当预期的回报结构发生变化时，退出都是理性选项之一。",
    ],
  },
  {
    id: "shen-jiashu-roadshow-controversy",
    title: "沈嘉树新剧路演状态引热议：是疲惫，还是态度问题？",
    excerpt:
      "一段八秒的台下花絮，让「沈嘉树」的路演状态成了讨论焦点。有人心疼行程太满，有人质疑敷衍观众，同一段画面被剪出了两种完全不同的结论。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20a%20movie%20press%20conference%20stage%20with%20empty%20chairs%20and%20warm%20backlight%2C%20quiet%20tired%20atmosphere%2C%20terracotta%20and%20amber%20palette%2C%20editorial%20cinema%20magazine%20visual%2C%20soft%20texture%20and%20grain&width=900&height=560&seq=mrcg-art-03&orientation=landscape",
    categorySlug: "actor-rumors",
    categoryName: "演员绯闻",
    author: "场记小周",
    date: "2026-09-07",
    readTime: 5,
    tags: ["演员绯闻", "剧组"],
    heat: 87,
    views: "74.8万",
    content: [
      "争议的起点是一段八秒钟的台下花絮。画面里，沈嘉树坐在主创席上低头看手机，主持人点名时他慢了半拍才抬头。这段画面被单独截出来配上文字后，迅速获得了百万播放。",
      "支持他的观众指出，那场路演是当天第三场，从早上十点持续到晚上九点，中间只有一次二十分钟的休息。把这样的行程强度称为「疲惫」并不过分，用八秒钟判断职业态度未免苛刻。",
      "质疑的一方则拿出了另一个视角：同一场次里，其他主创在互动环节都保持了较强的参与度，只有他多数时间处于低能量状态。对买票进场的观众来说，他们并不关心行程安排，只在意当下的体验。",
      "两种解读其实都有各自的合理性，争议的核心并不在这八秒，而在于「演员在宣传期应当承担多少情绪劳动」这个一直没有答案的问题。观众期待真诚，片方需要曝光，演员本人则要在连轴转的日程里维持状态。",
      "传播到第三天，沈嘉树本人在一场采访里回应了这件事，说自己那几天确实嗓子不适，没有把状态调整好，并为此致歉。这番回应没有激起更大的波澜，也算是给这场关于「疲惫与敷衍」的讨论画上了一个相对平稳的句号。",
    ],
  },
  {
    id: "ye-zhiqiu-director-collaboration",
    title: "演员叶知秋与导演合作传闻：剧组内部视角还原一场误会的诞生",
    excerpt:
      "同一张片场合照，在营销号那里成了「关系密切」的证据，在剧组工作人员看来却只是正常的收工留影。我们找了几位内部从业者，聊聊传闻是怎么长出来的。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20a%20warm%20film%20studio%20backlot%20at%20golden%20hour%20with%20lighting%20rigs%20and%20wooden%20crates%2C%20nostalgic%20cinema%20mood%2C%20terracotta%20amber%20pillow%20tones%2C%20editorial%20magazine%20visual%2C%20grainy%20texture&width=900&height=560&seq=mrcg-art-04&orientation=landscape",
    categorySlug: "actor-rumors",
    categoryName: "演员绯闻",
    author: "白桃",
    date: "2026-09-06",
    readTime: 6,
    tags: ["演员绯闻", "幕后"],
    heat: 82,
    views: "65.3万",
    content: [
      "照片本身很普通：剧组收工后，导演和几位主创站在布景前合影，叶知秋站在偏中间的位置。这样的照片在任何一部戏的杀青日都会拍上十几张。它之所以被单独拎出来，是因为配文里多了「关系密切」四个字。",
      "一位在组里负责场务的工作人员告诉我们，导演与主演高频沟通是常态，尤其是拍摄进入后半程，几乎每天都要单独对戏、调表演节奏。如果把这些正常的工作互动都解读为私人关系，那整个剧组大概没有一个人是「清白」的。",
      "营销号的叙事之所以有市场，是因为它符合大众对「剧组恋情」这类故事的既有想象。真实的工作场景往往是琐碎、重复甚至枯燥的，而传闻提供了一个更戏剧化的版本。",
      "叶知秋方面没有对传闻做出正面回应，只在一次线下活动里被问到时笑了笑，说「拍戏的时候导演确实骂我最多」。这句半开玩笑的回答，反而成了整件事里最接近事实的一句。",
      "我们想提醒的是，一张合照能承载的信息极其有限。在缺乏更多证据的情况下，把所有善意的工作关系都往情感方向联想，既不尊重从业者，也让讨论失去了价值。",
    ],
  },
  {
    id: "su-li-live-vocals-controversy",
    title: "歌手苏黎演唱会假唱疑云：现场音轨对比详解，我们到底能听出什么",
    excerpt:
      "一段粉丝拍摄的现场视频被逐帧分析，有人认定是假唱，也有人认为是混音问题。与其站队，不如先搞清楚现场音轨这件事到底有多复杂。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20concert%20stage%20with%20glowing%20golden%20spotlights%20and%20haze%2C%20singer%20silhouette%20holding%20a%20microphone%2C%20warm%20amber%20and%20crimson%20tones%2C%20music%20magazine%20illustration%2C%20cinematic%20depth%20and%20film%20grain&width=900&height=560&seq=mrcg-art-05&orientation=landscape",
    categorySlug: "singer-flops",
    categoryName: "歌手翻车",
    author: "后台调音师",
    date: "2026-09-05",
    readTime: 8,
    tags: ["歌手翻车", "现场"],
    heat: 91,
    views: "88.1万",
    content: [
      "这次争议的源头是一段不到一分钟的现场视频。视频里苏黎唱到副歌时嘴型与声音出现了轻微的错位，有网友把这段画面放慢并叠加了音轨波形，得出「假唱」的结论，相关解读视频播放量很快突破两百万。",
      "但把「嘴型错位」直接等同于「假唱」，在技术上并不严谨。大型演唱会的现场音频通常由多条音轨叠加而成：人声、和声、伴奏、垫音。垫音的存在本身就是行业惯例，问题在于它被推得多高、用得多不多。",
      "一位从业多年的调音师向我们解释，观众的座位位置、现场扩声的延迟、以及手机拍摄时的麦克风处理，都会让画面与声音产生肉眼可见的错位。换句话说，光凭一段观众拍摄的视频，很难得出确定结论。",
      "真正让争议升级的，是团队早期的沉默。在舆论最热的那两天，官方没有发布任何技术说明或现场多机位素材。信息真空往往会被各种猜测填满，这几乎是所有危机的通用剧本。",
      "到第五天，主办方公开了一段多机位现场素材，音画同步正常，事件逐渐平息。复盘整个过程，与其说这是一次「翻车」，不如说是一次关于「现场演出标准该如何向观众解释」的沟通失败。",
    ],
  },
  {
    id: "weekend-microphone-editing-highlights",
    title: "《周末开麦》名场面盘点：那些被剪掉又被网友翻出来的瞬间",
    excerpt:
      "一档已经播完的综艺，因为一段未播花絮重新回到话题中心。我们整理了被讨论最多的几个片段，看看观众到底在为什么意难平。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20retro%20variety%20show%20studio%20with%20colorful%20stage%20curtains%20and%20glowing%20bulbs%2C%20playful%20editorial%20energy%2C%20warm%20red%20orange%20and%20cream%20palette%2C%20vintage%20television%20poster%20style%2C%20grainy%20texture&width=900&height=560&seq=mrcg-art-06&orientation=landscape",
    categorySlug: "variety-moments",
    categoryName: "综艺名场面",
    author: "追综er",
    date: "2026-09-04",
    readTime: 5,
    tags: ["综艺名场面", "剪辑"],
    heat: 79,
    views: "58.7万",
    content: [
      "《周末开麦》收官已经两周，一段时长四分钟的花絮突然被翻了出来。花絮里几位嘉宾在游戏环节的对话被完整保留，其中一段即兴互动因为太过自然，反而成了这季被转发最多的片段。",
      "观众的意难平集中在一点：这么好看的内容，为什么没有剪进正片。评论区里出现频率最高的一句话是「正片太规整了，花絮才有活人感」。",
      "从制作角度看，综艺正片受限于时长与叙事节奏，必须服务于一条清晰的故事线。即兴的、碎片化的精彩往往因为无法嵌入主线而被舍弃。这不是剪辑师不懂观众，而是节目逻辑与观众期待之间天然存在落差。",
      "值得一提的是，这一季的讨论热度里，有相当一部分是由「未播内容」贡献的。平台逐渐意识到，花絮本身就是一个独立的内容产品，它满足的是观众对真实感的渴求。",
      "对观众来说，最值得关注的或许不是哪一个片段被剪了，而是我们为什么越来越在意那些「没有被设计过的瞬间」。综艺的魅力，很大程度上正来自这种不可控的真实。",
    ],
  },
  {
    id: "crisis-pr-first-48-hours",
    title: "艺人公关团队如何度过危机的 48 小时？一位前从业者的完整自述",
    excerpt:
      "热搜爆发的头两天，公关团队到底在做什么？从信息核实、口径统一到发布时机，一位曾服务过多位艺人的从业者还原了这套流程。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20a%20dim%20late%20night%20office%20with%20laptop%20glow%20and%20coffee%20cups%2C%20tense%20quiet%20atmosphere%2C%20warm%20amber%20desk%20lamp%20against%20deep%20crimson%20shadows%2C%20editorial%20magazine%20visual%2C%20film%20grain&width=900&height=560&seq=mrcg-art-07&orientation=landscape",
    categorySlug: "idol-meltdown",
    categoryName: "偶像塌房",
    author: "吃瓜编辑室",
    date: "2026-09-03",
    readTime: 9,
    tags: ["幕后", "公关"],
    heat: 76,
    views: "49.5万",
    content: [
      "「危机公关最忌讳的不是回应慢，而是回应乱。」这句话是陆扬（化名）在采访里反复强调的。他在娱乐行业做了八年公关，经历过大大小小几十次热搜危机，如今已经转行。",
      "按照他的描述，一条负面热搜出现后的第一个小时，团队做的第一件事根本不是写声明，而是核实。信息是否属实、信源是否可靠、有没有更多照片或视频在路上，这些判断直接决定了后续所有动作。",
      "第二个阶段是统一口径。他会同时联系艺人本人、经纪人与法务，确认哪些能说、哪些不能说。这个环节最耗时间，也最容易出问题，因为艺人在情绪状态下给出的说法，往往和事实有出入。",
      "第三个阶段才是发布。陆扬说，发布时机的选择甚至比内容本身更重要。如果证据即将被公开，抢先发布可以掌握叙事主动权；如果信息仍然模糊，过早回应反而会放大话题。",
      "他最后提到一个反直觉的观察：在绝大多数危机里，真正决定事件走向的不是公关话术，而是有没有更多新的证据出现。公关能做的，只是在一个已经确定的框架里，尽量让表达不失分。",
    ],
  },
  {
    id: "fan-economy-emotional-ledger",
    title: "从应援到脱粉：粉丝经济里的情感账本到底怎么记",
    excerpt:
      "为一个陌生人花掉半年积蓄，这件事在外人看来难以理解，在圈内却相当普遍。我们采访了几位不同阶段的粉丝，聊聊情感投入这件事的边界。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20scattered%20concert%20tickets%2C%20glow%20sticks%20and%20photo%20cards%20on%20a%20wooden%20table%2C%20nostalgic%20warm%20crimson%20and%20cream%20palette%2C%20editorial%20feature%20visual%2C%20soft%20natural%20light%20and%20grain&width=900&height=560&seq=mrcg-art-08&orientation=landscape",
    categorySlug: "idol-meltdown",
    categoryName: "偶像塌房",
    author: "白桃",
    date: "2026-09-02",
    readTime: 7,
    tags: ["粉丝文化", "观察"],
    heat: 72,
    views: "42.9万",
    content: [
      "小鹿今年二十七岁，追星九年。她算过一笔账：专辑、演唱会、应援集资、周边，这些年加起来大约花了八万多。她说这不是最多的，身边有人花了三倍不止。",
      "但当我们问她后不后悔时，她给出的答案很微妙：「不算后悔，但确实不像以前那么投入了。」她把这种变化形容为「从恋爱状态回到朋友状态」，不再要求对方回应，也就不会有失望。",
      "研究者常把粉丝投入分为两类：一类是消费性的，为作品和演出付费；另一类是情感性的，为身份认同和社群归属付费。前者有明确的价值交换，后者则更容易失衡。",
      "塌房之所以带来强烈冲击，正是因为它同时动摇了这两类投入。观众突然发现，自己付费购买的不只是作品，还有一整套关于「这个人是什么样的」叙事。当叙事崩塌，消费体验也随之受损。",
      "小鹿到现在仍然会看自己偶像的作品，只是不再买应援物了。「我还是喜欢他演的角色，但我不再假装我了解他。」这句话，或许是很多成年人追星之后最终抵达的地方。",
    ],
  },
  {
    id: "seven-years-since-debut-transition-anxiety",
    title: "选秀出道第七年，他们为什么集体陷入转型焦虑",
    excerpt:
      "同一批选秀出身的艺人，几乎在同一时间遇到了相似的困境。偶像生命周期到底有多长，转型这条路又为什么这么难走，我们试着拆开来看。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20an%20empty%20rehearsal%20studio%20with%20mirrors%20and%20wooden%20floor%2C%20warm%20afternoon%20sunlight%20through%20tall%20windows%2C%20terracotta%20and%20amber%20palette%2C%20editorial%20magazine%20visual%2C%20soft%20grain&width=900&height=560&seq=mrcg-art-09&orientation=landscape",
    categorySlug: "idol-meltdown",
    categoryName: "偶像塌房",
    author: "场记小周",
    date: "2026-09-01",
    readTime: 8,
    tags: ["行业观察", "偶像"],
    heat: 68,
    views: "37.4万",
    content: [
      "如果把选秀节目的出道年份排成一条时间线，会发现一个有趣的规律：同一批出道的艺人，往往会在第七年前后同时遇到瓶颈。合约周期、粉丝年龄结构、市场口味的变化，几乎都在这个节点叠加。",
      "偶像这份职业的特殊性在于，它对年龄和「新鲜感」的要求远高于其他表演行业。二十岁出头时，粉丝愿意为陪伴感买单；到了二十七岁，观众更关心你能不能拿出过硬的作品。",
      "转型的方向无非几条：演戏、综艺、音乐创作、幕后。但每一条都不轻松。演戏需要系统的表演训练，综艺依赖个人特质与临场反应，音乐创作则要求艺人真正具备作者身份，而不是只负责演唱。",
      "真正完成转型的艺人通常有一个共同点：他们在流量最鼎盛的时期就开始为「后流量时代」做准备，而不是等到热度下滑才开始焦虑。这种前瞻性，往往比天赋更能决定职业寿命。",
      "对观众来说，与其围观谁「糊了」，不如关注那些安静积累、逐步找到新位置的人。娱乐行业从不缺昙花一现，缺的是愿意慢下来打磨的人。",
    ],
  },
  {
    id: "music-festival-headliner-swap-dispute",
    title: "音乐节压轴临时换人，主办方与乐队各执一词的罗生门",
    excerpt:
      "距离压轴演出开场不到两小时，乐队突然宣布退出，主办方连夜找了替补。双方对外的说法针锋相对，而现场几万名观众只想问一句：票能退吗。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20outdoor%20music%20festival%20stage%20at%20night%20with%20crowd%20silhouettes%20and%20warm%20orange%20stage%20lights%2C%20dramatic%20sky%2C%20cinematic%20energy%2C%20amber%20and%20crimson%20palette%2C%20editorial%20music%20magazine%20visual%20with%20grain&width=900&height=560&seq=mrcg-art-10&orientation=landscape",
    categorySlug: "singer-flops",
    categoryName: "歌手翻车",
    author: "后台调音师",
    date: "2026-08-31",
    readTime: 6,
    tags: ["歌手翻车", "音乐节"],
    heat: 81,
    views: "60.3万",
    content: [
      "事情发生在一个周末的傍晚。距离压轴演出原定开场还有不到两小时，乐队官方账号突然发布一条简短声明，宣布因「不可抗力」退出当晚演出。消息发出后，现场观众席一片哗然。",
      "主办方随后回应，称乐队在演出前一天才提出临时增加的设备与费用要求，双方未能达成一致，才导致退出。而乐队方面则否认了这一说法，表示问题出在主办方未能履行此前的技术承诺。",
      "双方各执一词，且都拿不出完整证据。对于现场观众而言，这些细节并不重要，他们真正关心的是：已经购票却看不到心仪乐队，能否获得相应补偿。",
      "当晚主办方紧急安排了一组本地乐队替补压轴，并在第二天公布了部分退票方案。但方案的适用范围有限，只覆盖了购买压轴单日票的观众，通票观众不在其中，这又引发了新一轮不满。",
      "类似纠纷在音乐节行业并不罕见，根源往往在于演出合同对「不可抗力」与「技术条件」的界定过于模糊。当争议发生时，处于弱势的永远是已经付费的观众。",
    ],
  },
  {
    id: "variety-editing-controversy-audience-anger",
    title: "综艺剪辑争议再起：观众到底在为什么生气",
    excerpt:
      "一档热播综艺因为「乱剪」被骂上热搜。是剪辑真的有问题，还是观众对剧情走向不满意？我们找了剪辑师和观众两边的说法。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20a%20video%20editing%20timeline%20glowing%20on%20a%20dark%20screen%20with%20warm%20amber%20accents%2C%20modern%20studio%20atmosphere%2C%20cinematic%20mood%2C%20editorial%20magazine%20visual%2C%20soft%20grain%20and%20depth&width=900&height=560&seq=mrcg-art-11&orientation=landscape",
    categorySlug: "variety-moments",
    categoryName: "综艺名场面",
    author: "追综er",
    date: "2026-08-30",
    readTime: 6,
    tags: ["综艺名场面", "剪辑"],
    heat: 74,
    views: "45.1万",
    content: [
      "这一轮争议的直接导火索，是节目里一个明显的镜头跳接。嘉宾的表情从平静直接切到惊讶，中间的过程被省略，看起来像是被刻意制造了冲突。",
      "观众的愤怒有两个层面。第一层是技术上的：跳接让人出戏，破坏了观看体验。第二层是情感上的：观众觉得自己被剪辑引导了情绪，对嘉宾产生了不公正的印象。",
      "一位综艺剪辑师向我们解释，成片时长通常只有拍摄素材的百分之一，取舍是必须的。「制造冲突」有时不是恶意，而是因为冲突是叙事效率最高的方式。问题在于，当取舍牺牲了事实的完整性，就会伤害观众信任。",
      "近两年，越来越多节目开始公开未播花絮，试图用「完整版本」缓解争议。这其实是一种很好的平衡：正片负责叙事，花絮负责真实。",
      "对观众而言，意识到「你看的是经过剪辑的版本」，本身就是一种媒介素养。我们可以享受节目带来的戏剧性，但不必把节目里的每个人等同于他真实的样子。",
    ],
  },
  {
    id: "idol-contract-dispute-training-system",
    title: "偶像团体合约纠纷背后：练习生制度的中场战事",
    excerpt:
      "一场解约官司，把练习生制度里那些长期被默认的条款推到了台前。从训练成本分摊到收入分成，我们梳理了争议的几个关键点。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20a%20stack%20of%20unsigned%20contract%20papers%20and%20a%20pen%20on%20a%20dark%20desk%20under%20a%20single%20warm%20lamp%2C%20serious%20tense%20mood%2C%20crimson%20and%20amber%20accent%20lighting%2C%20editorial%20feature%20visual%20with%20grain&width=900&height=560&seq=mrcg-art-12&orientation=landscape",
    categorySlug: "idol-meltdown",
    categoryName: "偶像塌房",
    author: "吃瓜编辑室",
    date: "2026-08-29",
    readTime: 9,
    tags: ["行业观察", "合约"],
    heat: 70,
    views: "39.8万",
    content: [
      "这起纠纷最初只是一条简短的工作室声明，称某练习生单方面提出解约。随着双方陆续公开合约细节，讨论逐渐从「谁对谁错」转向「这套制度本身是否合理」。",
      "争议的焦点之一，是训练成本的归属。练习生在接受培训期间产生的声乐、舞蹈、造型等费用，通常由公司先行垫付，并在艺人出道后的收入中分期扣除。当解约发生时，这部分费用的结算往往成为争执重点。",
      "焦点之二，是长达数年的合约期限。在偶像行业，七到十年的合约并不罕见，而艺人的职业黄金期恰好落在这段时间内。合约的合理性，本质上取决于公司是否在期限内提供了足够的资源与被看见的机会。",
      "焦点之三，是收入分成的透明度。练习生出道后的收入往往需要经过多层级分配，艺人对最终到手金额的知情权，是这类纠纷反复出现的原因之一。",
      "无论判决结果如何，这起案件都让外界的目光第一次如此集中地落在练习生制度的具体条款上。讨论本身，或许就是推动行业规范化最有效的开始。",
    ],
  },
];

export const faqs: Faq[] = [
  {
    q: "「美日吃瓜」的内容多久更新一次？",
    a: "我们保持每日更新，工作日通常在上午与晚间各更新一轮，周末视热点情况灵活调整。四大频道（偶像塌房、演员绯闻、歌手翻车、综艺名场面）的热度榜也会同步刷新。",
  },
  {
    q: "网站上的艺人和事件是真实的吗？",
    a: "站内内容属于娱乐向创作与观察，人物姓名多为化名，事件用来说明娱乐行业的典型现象，请勿与真实个体一一对应，也不作为任何事实依据。我们更希望提供的是一种理性的吃瓜视角。",
  },
  {
    q: "为什么有些文章只有时间线，没有结论？",
    a: "娱乐事件在发酵初期信息往往不完整，我们选择先把可核实的时间线整理清楚，而不是急着下判断。当有新的进展时，我们会在文章内补充更新，并标注相应日期。",
  },
  {
    q: "如何订阅你们的热点更新？",
    a: "目前可以通过首页的热榜与频道页随时查看最新内容。你也可以在每篇文章底部查看标签，按标签浏览同类话题。订阅功能正在规划中，上线后会在站内公告。",
  },
  {
    q: "可以投稿或者提供线索吗？",
    a: "当然欢迎。你可以在「关于我们」页面找到联系邮箱，把线索、截图或你的观察发给我们。请在投稿时注明信息来源，我们会优先处理有据可查的内容。",
  },
  {
    q: "你们在内容上有什么原则？",
    a: "三条底线：不传播未经核实的私密信息，不对当事人进行人身攻击，不把未经证实的传闻当成事实陈述。我们关注的是事件与行业现象本身，而不是某个人。",
  },
];

export const testimonials = [
  {
    name: "追综多年的阿May",
    role: "老观众",
    quote:
      "最难得的是它不急着带节奏。很多热搜我都是先在这里看懂时间线，再去评论区看别人吵，心态稳多了。",
  },
  {
    name: "上班摸鱼的小陈",
    role: "通勤党",
    quote:
      "每天地铁上刷十分钟，四五个热点就都跟上了。作者把来龙去脉写得很清楚，不用自己去翻一堆碎片信息。",
  },
  {
    name: "影视从业者 Leo",
    role: "行业读者",
    quote:
      "那些讲公关、讲述练习生制度的稿子挺有意思，用的是行业内部视角，不是单纯围观，看得出是认真做过功课的。",
  },
  {
    name: "研究生小林",
    role: "粉丝文化观察者",
    quote:
      "把粉丝投入拆成情感和消费两层来解释，这个框架我第一次见是在这里看到，写论文的时候还引用过它的思路。",
  },
];